import {
	getPVSetupDevices,
	getPVSetupByType,
	getPVSetupHistory,
} from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
import type { ChartConfig, ChartDataInterface } from 'src/types/Chart';
import type { GetStatsResponseHistoryData } from 'src/types/requests/charts/get_stats_period';
import type { EnergyMonitoringPeriodId } from './period_helpers';
import { fetchEnergyStatistics } from './fetch_energy_statistics';
import { getActivePvSetupType } from './pv_flow_helpers';

export type PvEnergyHistoryResult = {
	history: GetStatsResponseHistoryData[];
	statistic?: never;
	interval?: string;
};

const PV_GENERATION_CHART_CONFIG: ChartConfig = {
	name: 'pv_generation',
	type: 'bar',
	series: [
		{
			visible: true,
			name: 'total_house_energy',
			style: 'overall_consumption',
		},
		{
			visible: true,
			name: 'generated_energy',
			style: 'generated_energy',
		},
	],
	units: 'Wh',
	height: 220,
};

const getConfiguredPvDevices = () => {
	const type = getActivePvSetupType();
	return { type, ...getPVSetupDevices(type) };
};

/**
 * Builds whole-house history from the configured PV wiring.
 * `total_house_energy` includes grid and self-consumed solar; `generated_energy`
 * contains the calculated output from all configured PV meters.
 */
export const fetchPvEnergyHistory = async (
	controls: ChartControlsDataInterface,
): Promise<PvEnergyHistoryResult | null> => {
	const { type, gridDevices, solarDevices } = getConfiguredPvDevices();
	if (!gridDevices.length || !solarDevices.length) return null;

	const result = await getPVSetupHistory(gridDevices, solarDevices, type, controls);
	return {
		interval: result.interval,
		history: result.history.map((entry) => ({
			...entry,
			consumption: Number(entry.total_house_energy ?? entry.consumption ?? 0),
			cost: Number(entry.grid_cost ?? entry.cost ?? 0),
		})),
	};
};

/** Builds a production-vs-house-load chart from the configured PV topology. */
export const fetchPvGenerationChart = async (
	controls: ChartControlsDataInterface,
): Promise<ChartDataInterface | null> => {
	const { type, gridDevices, solarDevices } = getConfiguredPvDevices();
	if (!gridDevices.length || !solarDevices.length) return null;

	return getPVSetupByType(gridDevices, solarDevices, type, PV_GENERATION_CHART_CONFIG, controls);
};

/**
 * Fetches whole-house consumption from the account endpoint and supplements it
 * with PV generation. PV device history must not replace account consumption:
 * configured PV meters can have a shorter retention window than the home total.
 */
export const fetchWholeHouseEnergyHistory = async (
	controls: ChartControlsDataInterface,
	periodId: EnergyMonitoringPeriodId,
) => {
	const [accountHistory, pvHistory] = await Promise.all([
		fetchEnergyStatistics(controls, periodId, undefined, {
			mergeLastHour: true,
			requireChartData: false,
		}),
		fetchPvEnergyHistory(controls),
	]);

	if (!accountHistory) return pvHistory;
	if (!pvHistory) return accountHistory;

	return {
		...accountHistory,
		history: [
			...accountHistory.history,
			...pvHistory.history.map((entry) => ({
				...entry,
				// These supplemental rows contribute generation only. Consumption and
				// cost remain authoritative fields from the account-wide response.
				consumption: 0,
				cost: 0,
				reversed: 0,
				export_energy: 0,
				purpose: 'pv_generation',
				generated_energy: entry.missing ? 0 : Number(entry.generated_energy ?? 0),
			})),
		],
	};
};
